<template>
  <div class="container-fluid">
    <h1 class="mb-3">Message Viewer</h1>

    <div class="row mb-3">
      
      <div class="col d-flex align-items-center">
        <button 
          type="button" 
          class="btn btn-sm dropdown-toggle me-auto" 
          :class="filtersActive ? 'btn-success' : 'btn-secondary'"
          @click="isFiltersVisible = !isFiltersVisible"
        >
          Filters
        </button>
        
        <button 
          type="button" 
          class="btn btn-sm btn-success" 
          @click="modalSend = true"
        >
          Send Message
        </button>
      </div>

      <ToggleLockedButton 
        v-if="auth.isAdmin" 
        v-model="includeLocked" 
        class="me-2"
        @toggle="toggleLockedFilter"
      />

    </div>

    <!-- Expandable Filters Section -->
    <div class="row mb-4">
      
      <div id="filtersInterface" class="collapse card" :class="{'show': isFiltersVisible}">
        <div class="card-body row g-3">
          
          <div class="col">
            <label class="form-label small fw-bold">Date Range:</label>
            <VueDatePicker 
              v-model="dateFilter" 
              @update:model-value="loadMessages(true)"
              :range="{ partialRange: false }" 
              :enable-time-picker="false"
            />
          </div>
          
          <div class="col">
            <div class="d-flex align-items-center">
              <label class="form-label small fw-bold me-2">Direction:</label>
              <span class="text-muted ms-2" style="font-size: 0.65rem;">
                (Ctrl + click to select multiple)
              </span>
              <div class="d-flex align-items-center gap-1 ms-auto">
                <button 
                  type="button" 
                  class="btn btn-sm p-0 d-inline-flex align-items-center justify-content-center" 
                  style="width: 16px; height: 16px; font-size: 15px; line-height: 1;"
                  aria-label="Apply Filter"
                  v-tooltip title="Apply" 
                  @click="loadMessages(true); $event.currentTarget.blur();"
                >
                  ✓
                </button>
                <button 
                  class="btn btn-sm btn-close"
                  v-tooltip title="Clear"  
                  @click="clearFilters('dir'); $event.target.blur();"
                />
              </div>
            </div>
            <select 
              v-model="directionFilter" 
              multiple 
              class="form-select form-select-sm" size="2"
            >
              <option v-for="dir in schemaTypes.MessageDirectionType" :key="dir" :value="dir">
                {{ dir }}
              </option>
            </select>
          </div>

          <div class="col">
            <div class="d-flex align-items-center">
              <label class="form-label small fw-bold me-2">State:</label>
              <span class="text-muted ms-2" style="font-size: 0.65rem;">
                (Ctrl + click to select multiple)
              </span>
              <div class="d-flex align-items-center gap-1 ms-auto">
                <button 
                  type="button" 
                  class="btn btn-sm p-0 d-inline-flex align-items-center justify-content-center" 
                  style="width: 16px; height: 16px; font-size: 15px; line-height: 1;"
                  aria-label="Apply Filter"
                  v-tooltip title="Apply" 
                  @click="loadMessages(true); $event.currentTarget.blur();"
                >
                  ✓
                </button>
                <button 
                  class="btn btn-sm btn-close"
                  v-tooltip title="Clear"  
                  @click="clearFilters('state'); $event.target.blur();"
                />
              </div>
            </div>
            <select 
              v-model="stateFilter" 
              multiple 
              class="form-select form-select-sm" size="4"
            >
              <option v-for="st in schemaTypes.MessageStateType" :key="st" :value="st">
                {{ st }}
              </option>
            </select>
          </div>

        </div>
      </div>
    </div>

    <SendMessageRawModal 
      v-if="modalSend" 
      :defaultImsi="lastImsi" 
      :defaultTransport="lastTransport" 
      @formDataSubmitted="sendMessage" 
      @formClosed="closedModal" 
    />

    <MessageForm 
      v-if="modalDetail" 
      tag="message" 
      msg="View Message" 
      :schema="messageSchema"
      :schemaTypes="schemaTypes" 
      :parentObj="detailMessage" 
      @formClosed="modalDetail = false" 
    />

    <MessageTable 
      modelName="Message"
      :columns="columns" 
      :ajaxUrl="messagesEndpoint"
      :orderBy="orderBy"
      :queryParams="messageQueryParams" 
      :options="tableOptions"
      ref="table"
    >
      <template #details="props">
        <TableActions mode="details" :rowData="props.rowData" @detail="openDetail" />
      </template>
    </MessageTable>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, computed, nextTick } from 'vue';
import VueDatePicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';

import { useAuthStore } from '@/stores/authStore';
import { useSchemaStore } from '@/stores/schemaStore';
import { ApiEndpoint } from '@/utils/config';
import { apiFetch } from '@/utils/api';
import { getCommonColumns, renderTime } from '@/utils/table';

import MessageTable from '@/components/TableComponent.vue';
import TableActions from '@/components/TableActions.vue';
import MessageForm from '@/components/DataFormModal.vue';
import SendMessageRawModal from '@/components/SendMessageRawModal.vue';
import ToggleLockedButton from '@/components/ToggleLockedButton.vue';

const auth = useAuthStore();
const schemaStore = useSchemaStore();

// --- State ---
const table = ref();

const isFiltersVisible = ref(false);
const dateFilter = ref([]);
const directionFilter = ref([]);
const stateFilter = ref([]);

const includeLocked = ref(false);

const isSendVisible = ref(false);
const modalSend = ref(false);
const sendToImsi = ref('');
const sendData = ref('');
const sendResult = ref('');

const modalDetail = ref(false);
const detailMessage = reactive({});
const messageSchema = ref([]);
const schemaTypes = ref({});

// --- Table Config ---
const columns = [
  {
    title: 'Timestamp',
    data: 'stateTime',
    width: '15%',
    tooltip: 'UTC',
    render: renderTime,
  },
  { title: 'Device ID', data: 'deviceId', width: '15%', className: 'td-truncate' },
  { title: 'IMSI', data: 'imsi', width: '15%' },
  { title: 'SIM APN', data: 'apnId', width: '15%' },
  {
    title: 'Transport',
    data: 'srcIp',
    width: '5%',
    render: (data, type, row) => {
      return (row.srcIp || row.dstIp) ? 'UDP' : 'NIDD';
    },
  },
  { title: 'Direction', data: 'direction', width: '5%' },
  { title: 'State', data: 'state', width: '5%' },
  { title: 'Size', data: 'size', className: 'override-center', defaultContent: '-', tooltip: 'Size in bytes' },
  { title: 'Customer ID', data: 'customerId', width: '10%', visible: auth.isAdmin },
  ...getCommonColumns(auth.isAdmin, true),
];

const orderBy = { stateTime: 'desc' };
const tableOptions = {
  fixedColumns: { left: 1, right: 1 } // Freeze Timestamp and Details
};

const messagesEndpoint = ApiEndpoint.MESSAGES;
const messageQueryParams = computed(() => {
  const params = {
    includeLocked: includeLocked.value ? includeLocked.value : undefined,
  };
  if (dateFilter.value && dateFilter.value.length) {
    const dateRange = dateFilter.value;
    params.from = new Date(new Date(dateRange[0])
      .setUTCHours(0, 0, 0, 0))
      .toISOString();
    params.to = new Date(new Date(dateRange[1])
      .setUTCHours(23, 59, 59, 999))
      .toISOString();
  }
  if (directionFilter.value && directionFilter.value.length) {
    if (directionFilter.value.length === 1) {
      params.direction = directionFilter.value[0];
    }
  }
  if (stateFilter.value && stateFilter.value.length) {
    params.state = stateFilter.value;
  }
  return params;
});

const filtersActive = computed(() => {
  if (dateFilter.value && dateFilter.value.length) return true;
  if (directionFilter.value && directionFilter.value.length) return true;
  if (stateFilter.value && stateFilter.value.length) return true;
  return false;
});

const clearFilters = async (filterType) => {
  // if (filterType === undefined || filterType === 'date') dateFilter.value = []; // automated by date picker
  if (filterType === undefined || filterType === 'dir') directionFilter.value = [];
  if (filterType === undefined || filterType === 'state') stateFilter.value = [];
  await loadMessages(true);
};

const toggleLockedFilter = async () => {
  await loadMessages(true);
};

// --- Methods ---
const loadMessages = async (forceRefresh = false) => {
  await nextTick();
  // Force DataTables to read parent boundaries and execute its first clean server network fetch
  if (table.value) {
    await table.value.reloadTable(forceRefresh);
  }
};

const openDetail = (row) => {
  Object.assign(detailMessage, row);
  modalDetail.value = true;
};

const closedModal = async () => {
  // 1. Instantly strip keyboard focus off the closing modal element
  if (document.activeElement && typeof document.activeElement.blur === 'function') {
    document.activeElement.blur();
  }
  // 2. Move focus to a safe, persistent target in the main view
  const deviceButton = document.getElementById('deviceSelector');
  if (deviceButton) {
    deviceButton.focus();
  }
  // 3. Clear state flag on the next tick loop once focus is clear
  await nextTick();
  modalSend.value = false;
  modalDetail.value = false;
  await loadMessages();
};

const lastImsi = computed(() => {
  const visible = table.value?.visibleRows || [];
  if (visible.length) return visible[0].imsi;
});

const lastTransport = computed(() => {
  const visible = table.value?.visibleRows || [];
  if (visible.length) return visible[0].srcIp ? 'UDP' : 'NIDD';
});

const sendMessage = async (formData) => {
  const { imsi, transport, payload } = formData;
  if (!imsi || !payload || !transport) {
    console.error(`Invalid message submission: ${JSON.stringify(formData)}`);
    return;
  }
  const dst = (formData.ipAddress || formData.ipPort)
    ? { address: formData.ipAddress || undefined, port: formData.ipPort || undefined}
    : undefined;
  const url = `${ApiEndpoint.MESSAGES}/downlink/` + `${transport}`.toLowerCase();
  try {
    const result = await apiFetch(url, {
      method: 'POST',
      body: { imsi, payload, dst },
    });
    console.info(`Send message result: ${JSON.stringify(result)}`);
    await loadMessages(true);
  } catch (err) {
    console.error(err);
  }
};

onMounted(() => {
  messageSchema.value = schemaStore.getFieldMeta('message');
  schemaTypes.value = schemaStore.getModelEnums('message');
});
</script>
<style scoped>
:deep(table.dataTable tr td.override-center) {
  text-align: center !important;
}
</style>